import * as z from "zod/v4";

export const DEFAULT_EMAIL_SENDER_NAME = "Offstreet Parking";

export const RFC_5322_EMAIL_REGEX =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Normalizes before validating so every consumer stores the canonical form
export const zodEmailValidation = z
	.string()
	.trim()
	.toLowerCase()
	.pipe(z.email({ pattern: RFC_5322_EMAIL_REGEX }));

export const zodEmailValidationOptional = z
	.preprocess((data) => (!data ? undefined : data), zodEmailValidation.optional())
	.optional();

export const isValidEmail = (email: string): boolean => {
	if (!email) {
		return false;
	}
	return zodEmailValidation.safeParse(email).success;
};

/**
 * One email domain: dot-separated labels of letters/digits/hyphens ending in a 2+ letter TLD,
 * with no leading or trailing hyphen on a label.
 *   matches:  example.com · sub.example.co.uk · my-co.io
 *   rejects:  example (no TLD) · -example.com / example-.com (edge hyphen) · example.c (1-char TLD)
 */
export const EMAIL_DOMAIN_REGEX = /^(([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})$/;

export const isValidEmailDomain = (domain: string): boolean => EMAIL_DOMAIN_REGEX.test(domain.trim().toLowerCase());

/**
 * Extracts the domain portion of an email, normalized for comparison
 * (trimmed and lowercased). Returns undefined when no domain is present.
 */
export const getEmailDomain = (email: string): string | undefined => {
	const atIndex = email.lastIndexOf("@");
	if (atIndex === -1) {
		return undefined;
	}
	const domain = email
		.slice(atIndex + 1)
		.trim()
		.toLowerCase();
	return domain || undefined;
};

/**
 * Decides whether an email's domain matches any of the given domains,
 * case-insensitively and whitespace-tolerant on both sides. Shared between the
 * portal client and server so their SSO domain matching cannot drift.
 */
export const isEmailInDomains = (email: string, domains: readonly string[]): boolean => {
	const emailDomain = getEmailDomain(email);
	if (!emailDomain) {
		return false;
	}
	return domains.some((domain) => domain.trim().toLowerCase() === emailDomain);
};

/**
 * Returns true when an additional-information field label should be treated as an email field.
 * This is intentionally label-based because additional-info fields are currently free-form.
 */
export const isAdditionalInfoEmailLabel = (label: string): boolean => {
	const normalizedLabel = label.toLowerCase();
	return normalizedLabel.includes("email") || normalizedLabel.includes("e-mail");
};
